// Debug overlay: ?debug=1 carica eruda da CDN per ispezione su mobile.
// In ogni caso (anche senza ?debug=1) cattura window.error + unhandledrejection
// in un ring buffer su window.__ALA_LOG__ così possiamo recuperarli dopo.
// Esponiamo anche window.__ALA_DIAG__ con info su browser/capability per la
// diagnosi di Giancarlo.

declare global {
  interface Window {
    __ALA_LOG__?: Array<{ ts: number; kind: string; msg: string; stack?: string }>;
    __ALA_DIAG__?: Record<string, unknown>;
    eruda?: { init: () => void };
  }
}

const MAX_LOG = 200;

function record(kind: string, msg: string, stack?: string) {
  const buf = (window.__ALA_LOG__ ??= []);
  buf.push({ ts: Date.now(), kind, msg, stack });
  while (buf.length > MAX_LOG) buf.shift();
}

function captureGlobalErrors() {
  window.addEventListener('error', (ev) => {
    record('error', ev.message || 'window.error', ev.error?.stack);
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const r = (ev.reason ?? {}) as { message?: string; stack?: string } | string;
    if (typeof r === 'string') record('unhandledrejection', r);
    else record('unhandledrejection', r.message ?? String(r), r.stack);
  });
}

function buildDiag(): Record<string, unknown> {
  const ua = navigator.userAgent;
  const md = navigator.mediaDevices;
  return {
    ua,
    platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform ?? null,
    isTouch: 'ontouchstart' in window,
    pixelRatio: window.devicePixelRatio,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    secureContext: window.isSecureContext,
    https: location.protocol === 'https:',
    hasMediaDevices: Boolean(md),
    hasGetUserMedia: Boolean(md?.getUserMedia),
    hasGetDisplayMedia: Boolean(md?.getDisplayMedia),
    hasEnumerateDevices: Boolean(md?.enumerateDevices),
    hasWebRTC: typeof RTCPeerConnection !== 'undefined',
    hasInsertableStreams: Boolean(window.RTCRtpSender && (RTCRtpSender.prototype as unknown as { createEncodedStreams?: unknown }).createEncodedStreams),
    permissionsApi: Boolean((navigator as Navigator & { permissions?: unknown }).permissions),
    storedDeviceIds: {
      mic: localStorage.getItem('ala-meet.micId'),
      cam: localStorage.getItem('ala-meet.camId'),
      spk: localStorage.getItem('ala-meet.spkId'),
    },
  };
}

function loadEruda(): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://cdn.jsdelivr.net/npm/eruda@3';
    s.crossOrigin = 'anonymous';
    s.referrerPolicy = 'no-referrer';
    s.onload = () => { try { window.eruda?.init(); resolve(); } catch (e) { reject(e); } };
    s.onerror = () => reject(new Error('eruda load failed'));
    document.head.appendChild(s);
  });
}

function showDiagBanner(diag: Record<string, unknown>) {
  const div = document.createElement('div');
  div.id = 'ala-debug-banner';
  div.style.cssText =
    'position:fixed;left:8px;right:8px;bottom:64px;z-index:99999;' +
    'background:#0a1124e6;color:#e8ecf5;padding:8px 10px;border-radius:8px;' +
    'font:600 11px/1.4 monospace;border:1px solid #b8842a;max-height:42vh;overflow:auto;';
  const lines = [
    `UA: ${String(diag.ua).slice(0, 90)}`,
    `secure=${diag.secureContext} https=${diag.https} touch=${diag.isTouch} dpr=${diag.pixelRatio}`,
    `vp=${(diag.viewport as { w: number; h: number }).w}x${(diag.viewport as { w: number; h: number }).h}`,
    `mediaDevices=${diag.hasMediaDevices} gUM=${diag.hasGetUserMedia} gDM=${diag.hasGetDisplayMedia} enum=${diag.hasEnumerateDevices}`,
    `WebRTC=${diag.hasWebRTC}`,
    `storedDevIds: mic=${(diag.storedDeviceIds as { mic: string | null }).mic ? 'sì' : 'no'} cam=${(diag.storedDeviceIds as { cam: string | null }).cam ? 'sì' : 'no'}`,
  ];
  div.innerHTML = lines.map((l) => `<div>${l}</div>`).join('') +
    `<div style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap">
      <button id="ala-dbg-clearids" style="background:#b8842a;color:#1b2d52;border:none;padding:3px 8px;border-radius:4px;font-weight:700">Reset device IDs</button>
      <button id="ala-dbg-log" style="background:#243a6a;color:#e8ecf5;border:1px solid #b8842a;padding:3px 8px;border-radius:4px;font-weight:700">Mostra log</button>
      <button id="ala-dbg-close" style="background:transparent;color:#e8ecf5;border:1px solid #8290ac;padding:3px 8px;border-radius:4px">Chiudi</button>
    </div>`;
  document.body.appendChild(div);

  div.querySelector<HTMLButtonElement>('#ala-dbg-clearids')!.addEventListener('click', () => {
    localStorage.removeItem('ala-meet.micId');
    localStorage.removeItem('ala-meet.camId');
    localStorage.removeItem('ala-meet.spkId');
    alert('Device IDs cancellati. Ricarica la pagina.');
  });
  div.querySelector<HTMLButtonElement>('#ala-dbg-log')!.addEventListener('click', () => {
    const buf = window.__ALA_LOG__ ?? [];
    const txt = buf.map((e) => `[${new Date(e.ts).toISOString().slice(11, 19)}] ${e.kind}: ${e.msg}${e.stack ? '\n' + e.stack : ''}`).join('\n\n');
    const w = window.open('', '_blank', 'noopener');
    if (w) { w.document.body.innerText = txt || '(nessun errore registrato)'; }
    else alert(txt || '(nessun errore registrato)');
  });
  div.querySelector<HTMLButtonElement>('#ala-dbg-close')!.addEventListener('click', () => div.remove());
}

function readDebugFlag(): '1' | '0' | null {
  const q = new URLSearchParams(location.search);
  if (q.has('debug')) return q.get('debug') === '0' ? '0' : '1';
  const frag = location.hash.replace(/^#/, '');
  if (frag) {
    const f = new URLSearchParams(frag);
    if (f.has('debug')) return f.get('debug') === '0' ? '0' : '1';
  }
  return null;
}
function isDebugOn(): boolean {
  const explicit = readDebugFlag();
  if (explicit === '1') return true;
  if (explicit === '0') return false;
  return localStorage.getItem('ala-meet.debug') === '1';
}

export function setupDebug(): void {
  captureGlobalErrors();
  const diag = buildDiag();
  window.__ALA_DIAG__ = diag;
  if (readDebugFlag() === '0') localStorage.removeItem('ala-meet.debug');
  if (isDebugOn()) {
    localStorage.setItem('ala-meet.debug', '1');
    showDiagBanner(diag);
    loadEruda().catch((e) => {
      record('eruda', 'load failed: ' + (e as Error).message);
      console.warn('[ala] eruda non caricato, uso solo banner', e);
    });
  }
}
